import { Injectable } from '@angular/core';
import { jwtPayload } from '../../shared/commons/token-payload';

@Injectable({
  providedIn: 'root'
})
export class PermissionService {

  private readonly PERMISSIONS_STORAGE_KEY = 'user_permissions';

  constructor() { }

  /**
   * Verifica si el usuario actual tiene un permiso específico
   * @param module El módulo (ej: 'orders', 'customers', etc.)
   * @param action La acción (ej: 'view', 'create', 'edit', etc.)
   * @returns true si tiene el permiso, false en caso contrario
   */
  hasPermission(module: string, action: string): boolean {
    const payload = jwtPayload();
    
    // Si no hay token, no tiene permisos
    if (!payload) {
      return false;
    }

    // Intentar obtener roles de ambas propiedades
    const userRoles = payload.roles || payload.role || '';

    // Admin siempre tiene todos los permisos
    if (userRoles.includes('Admin')) {
      return true;
    }

    // Construir el permiso en formato "module.action"
    const permissionValue = `${module.toLowerCase()}.${action.toLowerCase()}`;
    
    // Verificar en permisos almacenados en localStorage
    const storedPermissions = this.getStoredPermissions();
    if (storedPermissions.includes(permissionValue)) {
      return true;
    }

    // Verificar en el JWT (por compatibilidad)
    const jwtPermissions = (payload as any).permissions;
    if (jwtPermissions && Array.isArray(jwtPermissions)) {
      // Soportar tanto formato de string ("orders.view") como objeto { module, action }
      return jwtPermissions.some(p => {
        if (typeof p === 'string') {
          return p.toLowerCase() === permissionValue;
        }
        return p.module?.toLowerCase() === module.toLowerCase() && 
               p.action?.toLowerCase() === action.toLowerCase();
      });
    }

    return false;
  }

  /**
   * Verifica si el usuario tiene un permiso en formato "module.action"
   * @param permissionValue El permiso en formato "module.action" (ej: "orders.view")
   */
  hasPermissionValue(permissionValue: string): boolean {
    const payload = jwtPayload();
    
    if (!payload) {
      return false;
    }

    const userRoles = payload.roles || payload.role || '';
    if (userRoles.includes('Admin')) {
      return true;
    }

    // Verificar en permisos almacenados
    const storedPermissions = this.getStoredPermissions();
    return storedPermissions.includes(permissionValue.toLowerCase());
  }

  /**
   * Verifica si el usuario tiene alguno de los permisos especificados
   */
  hasAnyPermission(requiredPermissions: { module: string, action: string }[]): boolean {
    return requiredPermissions.some(perm => this.hasPermission(perm.module, perm.action));
  }

  /**
   * Verifica si el usuario tiene todos los permisos especificados
   */
  hasAllPermissions(requiredPermissions: { module: string, action: string }[]): boolean {
    return requiredPermissions.every(perm => this.hasPermission(perm.module, perm.action));
  }

  /**
   * Verifica si el usuario tiene un rol específico
   */
  hasRole(roleName: string): boolean {
    const payload = jwtPayload();
    if (!payload) {
      return false;
    }
    
    const userRoles = payload.roles || payload.role || '';
    return userRoles.includes(roleName);
  }

  /**
   * Obtiene todos los roles del usuario actual
   */
  getUserRoles(): string[] {
    const payload = jwtPayload();
    if (!payload) {
      return [];
    }
    
    const rolesString = payload.roles || payload.role || '';
    if (!rolesString) {
      return [];
    }
    
    return rolesString.split(', ');
  }

  /**
   * Almacena los permisos del usuario en localStorage
   * @param permissions Array de permisos en formato "module.action"
   */
  storePermissions(permissions: string[]): void {
    try {
      localStorage.setItem(this.PERMISSIONS_STORAGE_KEY, JSON.stringify(permissions));
    } catch (e) {
      console.error('Error storing permissions:', e);
    }
  }

  /**
   * Obtiene los permisos almacenados del usuario
   */
  getStoredPermissions(): string[] {
    try {
      const stored = localStorage.getItem(this.PERMISSIONS_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error('Error reading stored permissions:', e);
      return [];
    }
  }

  /**
   * Limpia los permisos almacenados (útil al cerrar sesión)
   */
  clearStoredPermissions(): void {
    try {
      localStorage.removeItem(this.PERMISSIONS_STORAGE_KEY);
    } catch (e) {
      console.error('Error clearing stored permissions:', e);
    }
  }

  /**
   * Obtiene todos los permisos del usuario (combinando JWT y localStorage)
   */
  getAllPermissions(): string[] {
    const payload = jwtPayload();
    const permissions: string[] = [];

    // Permisos del localStorage
    permissions.push(...this.getStoredPermissions());

    // Permisos del JWT
    if (payload) {
      const jwtPermissions = (payload as any).permissions;
      if (jwtPermissions && Array.isArray(jwtPermissions)) {
        jwtPermissions.forEach(p => {
          if (typeof p === 'string') {
            if (!permissions.includes(p)) {
              permissions.push(p);
            }
          } else if (p.module && p.action) {
            const permValue = `${p.module.toLowerCase()}.${p.action.toLowerCase()}`;
            if (!permissions.includes(permValue)) {
              permissions.push(permValue);
            }
          }
        });
      }
    }

    return permissions;
  }
}
